import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ErrorState, LoadingState } from '../../components/ui/States';
import type { Task } from '../../api/organizer';
import { useOrganizerCalendar } from '../../hooks/useOrganizer';
import {
  WEEKDAYS,
  isoLocal,
  monthGridDays,
  monthLabel,
  monthOfIso,
} from '../../utils/calendarGrid';
import { CHIPS_PER_CELL, chipClasses, chipLabel, groupByDay, taskIdOf } from './calendarUtils';
import { DayDetailPanel } from './DayDetailPanel';
import { dueLabel, isOverdue } from './taskUtils';

interface CalendarViewProps {
  onOpenTask: (taskId: string) => void;
  /** The board's tasks, so overdue ones dated before this month stay in view. */
  tasks: Task[];
  /** Switch the To-dos tab to the List view. */
  onShowList?: () => void;
}

/**
 * A month of your organizer items — tasks and reminders with a date. Google
 * meetings are left out on purpose: they already live in Google Calendar, and
 * mixing them in buried the to-dos.
 *
 * All the date maths is either in `calendarGrid` (pure strings) or already done by
 * the API, which ships each item's Brussels days and time label. This component
 * performs no timezone conversion, which is the only way the grid, the 08:15
 * digest and the nav badge can agree about what "today" means.
 */
export function CalendarView({ onOpenTask, tasks, onShowList }: CalendarViewProps) {
  const [anchor, setAnchor] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  // An empty month collapses to one line; this opens the grid anyway (to pick a day).
  const [showEmptyGrid, setShowEmptyGrid] = useState(false);

  const grid = useMemo(() => monthGridDays(anchor), [anchor]);
  const { data, isLoading, isFetching, isError, error, refetch } = useOrganizerCalendar(
    grid.from,
    grid.to,
  );

  const items = useMemo(() => (data?.items ?? []).filter((item) => item.kind !== 'meeting'), [data]);
  const byDay = useMemo(() => groupByDay(items), [items]);
  const today = data?.today ?? isoLocal(new Date());
  const isCurrentMonth = monthGridDays(new Date()).from === grid.from;
  const monthStart = `${grid.year}-${String(grid.month).padStart(2, '0')}-01`;
  // Overdue to-dos from before the month on screen would otherwise vanish from
  // this view entirely; they get a strip above the grid instead.
  const earlierOverdue = useMemo(
    () =>
      tasks
        .filter((t) => isOverdue(t) && t.dueDate !== null && t.dueDate < monthStart)
        .sort((a, b) => (a.dueDate! < b.dueDate! ? -1 : 1)),
    [tasks, monthStart],
  );

  // Open to-dos with no date never land on the grid; a tray keeps them in view.
  const undated = useMemo(() => tasks.filter((t) => t.status !== 'done' && !t.dueDate), [tasks]);
  const openCount = useMemo(() => tasks.filter((t) => t.status !== 'done').length, [tasks]);
  // Every open to-do is undated: the grid can only ever be empty, so say so
  // up front instead of leaving an empty month to read as "nothing to do".
  const noneDated = openCount > 0 && undated.length === openCount;
  const [trayToggled, setTrayOpen] = useState<boolean | null>(null);
  const trayOpen = trayToggled ?? noneDated;

  function shiftMonth(delta: number) {
    setSelectedDay(null);
    setAnchor((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <NavButton label="Previous month" onClick={() => shiftMonth(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </NavButton>
          <h2 className="min-w-[9rem] text-center text-body-sm font-medium text-text-primary">
            {monthLabel(anchor)}
          </h2>
          <NavButton label="Next month" onClick={() => shiftMonth(1)}>
            <ChevronRight className="h-4 w-4" />
          </NavButton>
          {!isCurrentMonth && (
            <button
              type="button"
              onClick={() => {
                setAnchor(new Date());
                setSelectedDay(null);
              }}
              className="ml-2 rounded-lg px-2 py-1 text-body-xs text-text-tertiary hover:bg-surface-secondary hover:text-text-primary"
            >
              Today
            </button>
          )}
        </div>

        {/* A background refetch must never blank the grid. */}
        {isFetching && !isLoading && (
          <span className="text-body-xs text-text-muted">Syncing…</span>
        )}
      </div>

      {noneDated && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border-light bg-surface-secondary px-3 py-2">
          <p className="text-body-xs text-text-secondary">
            <span className="font-medium text-text-primary">
              {openCount} open, none dated.
            </span>{' '}
            The calendar only shows to-dos with a due date. They're listed under "No date" below.
          </p>
          {onShowList && (
            <button
              type="button"
              onClick={onShowList}
              className="rounded px-2 py-1 text-body-xs font-medium text-text-secondary hover:bg-surface-tertiary hover:text-text-primary"
            >
              Show as list
            </button>
          )}
        </div>
      )}

      {earlierOverdue.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-error/20 bg-error-light/40 px-3 py-2">
          <span className="text-body-xs font-medium text-error-dark">
            Overdue from earlier · {earlierOverdue.length}
          </span>
          {earlierOverdue.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onOpenTask(t.id)}
              title={t.title}
              className="inline-flex max-w-full items-baseline gap-1.5 rounded-sm bg-surface-primary px-2 py-0.5 text-left text-body-xs text-text-primary hover:bg-surface-secondary"
            >
              {/* Wraps instead of cutting the title off */}
              <span className="min-w-0 break-words">{t.title}</span>
              <span className="flex-none tabular-nums text-error-dark">{dueLabel(t.dueDate)}</span>
            </button>
          ))}
        </div>
      )}

      {isError ? (
        <ErrorState title="Couldn’t load your calendar" error={error} onRetry={() => void refetch()} />
      ) : !isLoading && items.length === 0 && !showEmptyGrid ? (
        // Nothing dated this month: one line instead of an empty month of boxes.
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border-light px-3 py-2">
          <p className="text-body-xs text-text-tertiary">
            Nothing dated in {monthLabel(anchor)}. To-dos and reminders with a date show up here.
          </p>
          <button
            type="button"
            onClick={() => setShowEmptyGrid(true)}
            className="rounded px-2 py-1 text-body-xs font-medium text-text-secondary hover:bg-surface-secondary hover:text-text-primary"
          >
            Show the month
          </button>
        </div>
      ) : (
        <div className="space-y-1.5">
          <div className="overflow-hidden rounded-xl border border-border-light">
            <div className="grid grid-cols-7 border-b border-border-light bg-surface-secondary">
              {WEEKDAYS.map((day) => (
                <div
                  key={day}
                  className="px-2 py-1.5 text-center text-body-xs font-medium text-text-tertiary"
                >
                  {day}
                </div>
              ))}
            </div>

            {isLoading ? (
              <div className="min-h-[420px] p-4">
                <LoadingState rows={5} />
              </div>
            ) : (
              <div className="grid grid-cols-7">
                {grid.days.map((day) => (
                  <DayCell
                    key={day}
                    day={day}
                    items={byDay.get(day) ?? []}
                    inMonth={monthOfIso(day) === grid.month}
                    isToday={day === today}
                    isSelected={day === selectedDay}
                    onSelect={() => setSelectedDay(day === selectedDay ? null : day)}
                    onOpenTask={onOpenTask}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {undated.length > 0 && (
        <div className="rounded-xl border border-border-light bg-surface-secondary px-3 py-2">
          <button
            type="button"
            onClick={() => setTrayOpen(!trayOpen)}
            aria-expanded={trayOpen}
            className="flex items-center gap-1 text-body-xs font-medium text-text-secondary hover:text-text-primary"
          >
            <ChevronRight className={clsx('h-3.5 w-3.5 transition-transform', trayOpen && 'rotate-90')} aria-hidden />
            No date ({undated.length})
          </button>
          {trayOpen && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {undated.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onOpenTask(t.id)}
                  title={t.title}
                  className="max-w-[16rem] truncate rounded-sm bg-surface-primary px-2 py-0.5 text-body-xs text-text-primary hover:bg-surface-tertiary"
                >
                  {t.title}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {selectedDay && (
        <DayDetailPanel
          day={selectedDay}
          items={byDay.get(selectedDay) ?? []}
          isToday={selectedDay === today}
          onOpenTask={onOpenTask}
          onClose={() => setSelectedDay(null)}
        />
      )}
    </div>
  );
}

function NavButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="rounded-lg px-2 py-1 text-body-sm text-text-tertiary hover:bg-surface-secondary hover:text-text-primary"
    >
      {children}
    </button>
  );
}

interface DayCellProps {
  day: string;
  items: ReturnType<typeof groupByDay> extends Map<string, infer V> ? V : never;
  inMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onOpenTask: (taskId: string) => void;
}

function DayCell({ day, items, inMonth, isToday, isSelected, onSelect, onOpenTask }: DayCellProps) {
  const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
  const isWeekend = weekday === 0 || weekday === 6;
  const visible = items.slice(0, CHIPS_PER_CELL);
  const hidden = items.length - visible.length;

  return (
    <div
      className={clsx(
        'min-h-[104px] border-b border-r border-border-light p-1.5 last:border-r-0',
        isWeekend ? 'bg-surface-secondary/60' : 'bg-surface-primary',
        !inMonth && 'opacity-45',
        isSelected && 'ring-2 ring-inset ring-text-primary',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        className={clsx(
          'mb-1 flex h-5 w-5 items-center justify-center rounded-full text-body-xs',
          isToday
            ? 'bg-text-primary font-semibold text-text-inverse'
            : 'text-text-tertiary hover:bg-surface-tertiary hover:text-text-primary',
        )}
      >
        {Number(day.slice(8, 10))}
      </button>

      <div className="space-y-1">
        {visible.map((item) => {
          const taskId = taskIdOf(item);
          const label = chipLabel(item);
          const chip = (
            <span
              className={clsx(
                'block truncate rounded px-1.5 py-0.5 text-left text-[11px] leading-tight',
                chipClasses(item),
              )}
              title={label}
            >
              {label}
            </span>
          );

          if (taskId) {
            return (
              <button key={item.id} type="button" onClick={() => onOpenTask(taskId)} className="block w-full">
                {chip}
              </button>
            );
          }
          return item.href ? (
            <a
              key={item.id}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full"
            >
              {chip}
            </a>
          ) : (
            <div key={item.id}>{chip}</div>
          );
        })}

        {hidden > 0 && (
          <button
            type="button"
            onClick={onSelect}
            className="block w-full px-1.5 text-left text-[11px] text-text-tertiary hover:text-text-primary"
          >
            +{hidden} more
          </button>
        )}
      </div>
    </div>
  );
}
